import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { formatMoney, formatMoneyShort } from "../../lib/money";
import { SplitBar } from "../../components/charts";
import { AddButton, ErrorNote } from "../../components/ui";
import { balanceIsOwed } from "../../lib/dealStatus";
import { listOpenWeOwes, type OpenWeOwe } from "../deals/weOwes";
import { allOpenPromises, type OpenPromise } from "../deals/promises";
import { usDate } from "../../forms/mappings";

/**
 * The landing page. Not a wall of charts: the things worth knowing on the way
 * in — what is on the lot, what money is tied up in it, what is waiting on a
 * title, and which deals are still owed money.
 */

interface V {
  id: string; stock_no: string; year: number; make: string; model: string;
  odometer: number; total_cost_cents: number; asking_price_cents: number;
  status: string; title_status: string; purchase_date: string;
}
interface D {
  id: string; deal_no: string; status: string; agreement_date: string;
  created_at: string; cash_price_cents: number;
  total_amount_due_cents: number;
  buyer: { first_name: string; last_name: string } | null;
  vehicle: { year: number; make: string; model: string;
             total_cost_cents: number } | null;
}

const days = (iso: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 864e5));

const OPEN = ["quote", "deposit"];
const LIVE = ["purchased", "in_recon", "available", "on_hold"];

export default function Home({ go, onOpenDeal }: {
  go: (v: "lot" | "deals" | "customers" | "addVehicle" | "newDeal") => void;
  /** So a promise on the list opens the deal it was made on, rather than
      leaving somebody to search for it. */
  onOpenDeal: (id: string) => void;
}) {
  const [vehicles, setVehicles] = useState<V[] | null>(null);
  const [deals, setDeals] = useState<D[] | null>(null);
  const [owing, setOwing] = useState<Record<string, number>>({});
  const [weOwes, setWeOwes] = useState<OpenWeOwe[]>([]);
  const [due, setDue] = useState<OpenPromise[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      supabase.from("vehicles")
        .select(`id,stock_no,year,make,model,odometer,total_cost_cents,
                 asking_price_cents,status,title_status,purchase_date`),
      supabase.from("deals")
        .select(`id,deal_no,status,agreement_date,created_at,cash_price_cents,
                 total_amount_due_cents,
                 buyer:customers!deals_buyer_id_fkey(first_name,last_name),
                 vehicle:vehicles!deals_vehicle_id_fkey(year,make,model,total_cost_cents)`)
        .order("created_at", { ascending: false }),
    ]).then(([v, d]) => {
      if (v.error || d.error) { setErr("Could not load the dashboard."); return; }
      setVehicles((v.data ?? []) as V[]);
      setDeals((d.data ?? []) as unknown as D[]);
    });

    // What is actually owed, from payments received.
    supabase.from("deal_balances").select("id,owing_cents").then(({ data }) => {
      const map: Record<string, number> = {};
      for (const b of data ?? []) map[b.id as string] = b.owing_cents as number;
      setOwing(map);
    });

    /* Promises made to buyers. This is the one thing on the dashboard that
       is somebody waiting on the dealership rather than the reverse, so a
       failure to load it is not swallowed. */
    listOpenWeOwes()
      .then(setWeOwes)
      .catch((e: Error) => setErr(e.message));

    /* Balances the buyer said they would bring. The other direction from the
       we-owes above, and the one that is money rather than work. Not
       swallowed either: a date nobody can see is the whole problem this
       solves. */
    allOpenPromises()
      .then(setDue)
      .catch((e: Error) => setErr(e.message));
  }, []);

  const stats = useMemo(() => {
    if (!vehicles || !deals) return null;
    const onLot = vehicles.filter((v) => LIVE.includes(v.status));
    const tiedUp = onLot.reduce((n, v) => n + v.total_cost_cents, 0);
    const noTitle = onLot.filter((v) => v.title_status === "not_received");
    const aged = [...onLot].sort((a, b) =>
      days(b.purchase_date) - days(a.purchase_date));

    const open = deals.filter((d) => OPEN.includes(d.status));
    const owed = deals
      .filter((d) => balanceIsOwed(d.status) && (owing[d.id] ?? 0) > 0);
    const outstanding = owed.reduce((n, d) => n + (owing[d.id] ?? 0), 0);

    const now = new Date();
    const soldThisMonth = deals.filter((d) => {
      if (d.status !== "sold") return false;
      const t = new Date(d.agreement_date + "T00:00:00");
      return t.getMonth() === now.getMonth() && t.getFullYear() === now.getFullYear();
    });
    const grossThisMonth = soldThisMonth.reduce(
      (n, d) => n + (d.cash_price_cents - (d.vehicle?.total_cost_cents ?? 0)), 0);

    return { onLot, tiedUp, noTitle, aged, open, owed, outstanding,
             soldThisMonth, grossThisMonth };
  }, [vehicles, deals, owing]);

  if (err) return <ErrorNote>{err}</ErrorNote>;
  if (!stats) return <p className="hint">Loading</p>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const attention: { text: string; action: () => void; tone: string }[] = [];
  if (stats.noTitle.length)
    attention.push({
      tone: "amber",
      text: `${stats.noTitle.length} ${stats.noTitle.length === 1 ? "car" : "cars"} on the lot without a title`,
      action: () => go("lot"),
    });
  if (stats.owed.length)
    attention.push({
      tone: "amber",
      text: `${formatMoney(stats.outstanding)} outstanding across ${stats.owed.length} ${stats.owed.length === 1 ? "deal" : "deals"}`,
      action: () => go("deals"),
    });
  const lateOwes = weOwes.filter((w) => w.overdue);
  if (lateOwes.length)
    attention.push({
      tone: "amber",
      text: `${lateOwes.length} ${lateOwes.length === 1 ? "promise is" : "promises are"} past the date we gave the buyer`,
      action: () => onOpenDeal(lateOwes[0]!.deal_id),
    });

  const lateDue = due.filter((p) => p.overdue);
  if (lateDue.length)
    attention.push({
      tone: "amber",
      text: `${formatMoney(lateDue.reduce((n, p) => n + p.chase_cents, 0))} was due `
          + `on a date that has passed — ${lateDue.length} `
          + `${lateDue.length === 1 ? "buyer" : "buyers"}`,
      action: () => onOpenDeal(lateDue[0]!.deal_id),
    });

  const dueToday = due.filter((p) => p.days_away === 0);
  if (dueToday.length)
    attention.push({
      tone: "sky",
      text: `${formatMoney(dueToday.reduce((n, p) => n + p.chase_cents, 0))} is due in today`,
      action: () => onOpenDeal(dueToday[0]!.deal_id),
    });

  const stale = stats.aged.filter((v) => days(v.purchase_date) >= 90);
  if (stale.length)
    attention.push({
      tone: "grey",
      text: `${stale.length} ${stale.length === 1 ? "car has" : "cars have"} been on the lot 90 days or more`,
      action: () => go("lot"),
    });

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>{greeting}</h2>
          <p className="pagehead-sub">
            {new Date().toLocaleDateString("en-US",
              { weekday: "long", month: "long", day: "numeric" })}
          </p>
        </div>
        <div className="head-actions">
          <AddButton label="New deal" onClick={() => go("newDeal")} />
        </div>
      </div>

      {/* How the lot divides, before any of the numbers. It answers "what is
          the shape of my inventory" in one line, which four separate tiles
          never did. */}
      {stats.onLot.length > 0 && (
        <section className="panel record">
          <div className="panel-head">
            <h3>Where the lot stands</h3>
            <span className="panel-note">
              {stats.onLot.length} {stats.onLot.length === 1 ? "vehicle" : "vehicles"}
            </span>
          </div>
          <div className="panel-body">
            <SplitBar parts={[
              { label: "Available", tone: "ok",
                value: stats.onLot.filter((v) => v.status === "available").length },
              { label: "In recon", tone: "amber",
                value: stats.onLot.filter((v) => v.status === "in_recon").length },
              { label: "On deposit", tone: "sky",
                value: stats.onLot.filter((v) => v.status === "on_hold").length },
              { label: "Just bought", tone: "navy",
                value: stats.onLot.filter((v) => v.status === "purchased").length },
            ]} />
          </div>
        </section>
      )}

      <div className="tiles">
        <button className="tile" onClick={() => go("lot")}>
          <span className="tile-n">{stats.onLot.length}</span>
          <span className="tile-l">on the lot</span>
          <span className="tile-s">{formatMoneyShort(stats.tiedUp)} tied up</span>
        </button>
        <button className="tile" onClick={() => go("deals")}>
          <span className="tile-n">{stats.open.length}</span>
          <span className="tile-l">open {stats.open.length === 1 ? "deal" : "deals"}</span>
          <span className="tile-s">
            {stats.outstanding > 0
              ? `${formatMoneyShort(stats.outstanding)} owing`
              : "nothing owing"}
          </span>
        </button>
        <button className="tile" onClick={() => go("deals")}>
          <span className="tile-n">{stats.soldThisMonth.length}</span>
          <span className="tile-l">sold this month</span>
          <span className="tile-s">
            {stats.soldThisMonth.length
              ? `${formatMoneyShort(stats.grossThisMonth)} gross`
              : "nothing yet"}
          </span>
        </button>
        {/* tile-alert, not "flag". `.flag` is the little grey pill used for
            badges elsewhere, and as a bare class it overrode this button's
            display, background, radius and padding — the number and the label
            collapsed onto one line inside a grey lozenge. It only showed once
            a car actually lacked a title, which is why it survived this long. */}
        <button className={`tile ${stats.noTitle.length ? "tile-alert" : ""}`}
                onClick={() => go("lot")}>
          <span className="tile-n">{stats.noTitle.length}</span>
          <span className="tile-l">without title</span>
          {/* The other three tiles carry a third line. Without one this tile
              sat short against them in the row. */}
          <span className="tile-s">
            {stats.noTitle.length ? "waiting on paperwork" : "all titled"}
          </span>
        </button>
      </div>

      {attention.length > 0 && (
        <section className="panel">
          <h3 className="panel-h">Worth a look</h3>
          <ul className="attn">
            {attention.map((a) => (
              <li key={a.text}>
                <button className={`attn-row ${a.tone}`} onClick={a.action}>
                  <span className="dot" />
                  {a.text}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {due.length > 0 && (
        <section className={`panel ${lateDue.length ? "danger" : "money"}`}>
          <div className="panel-top">
            <h3 className="panel-h">Balances coming in</h3>
            <span className="panel-note">
              {formatMoney(due.reduce((n, p) => n + p.chase_cents, 0))} across{" "}
              {due.length} {due.length === 1 ? "deal" : "deals"}
            </span>
          </div>
          <ul className="mini">
            {due.slice(0, 6).map((p) => (
              <li key={p.id}>
                <button className="mini-row" onClick={() => onOpenDeal(p.deal_id)}>
                  <span className="mini-a">
                    {p.customer_name}
                    {/* The phone is here because chasing a balance is a phone
                        call, and looking it up is the step that stops it
                        happening. */}
                    <em>{p.vehicle ?? "—"}{p.customer_phone ? ` · ${p.customer_phone}` : ""}</em>
                  </span>
                  <span className="mini-b">
                    <span className={p.overdue ? "flag late" : "flag"}>
                      {p.overdue
                        ? `${Math.abs(p.days_away)}d late`
                        : p.days_away === 0 ? "today" : usDate(p.promised_for)}
                    </span>
                    <em>{formatMoney(p.chase_cents)}</em>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {due.length > 6 && (
            <p className="hint">and {due.length - 6} more.</p>
          )}
        </section>
      )}

      {weOwes.length > 0 && (
        <section className="panel work">
          <div className="panel-top">
            <h3 className="panel-h">What we owe buyers</h3>
            <span className="panel-note">
              {weOwes.length} outstanding
            </span>
          </div>
          <ul className="mini">
            {weOwes.slice(0, 6).map((w) => (
              <li key={w.id}>
                <button className="mini-row" onClick={() => onOpenDeal(w.deal_id)}>
                  <span className="mini-a">
                    {w.title}
                    <em>{w.buyer_name} — {w.vehicle}</em>
                  </span>
                  <span className="mini-b">
                    {w.due_date
                      ? <span className={w.overdue ? "flag late" : "flag"}>
                          {w.overdue ? "overdue" : usDate(w.due_date)}
                        </span>
                      : <span className="flag">no date</span>}
                    <em>{w.deal_no}</em>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {weOwes.length > 6 && (
            <p className="hint">and {weOwes.length - 6} more.</p>
          )}
        </section>
      )}

      <div className="cols">
        <section className="panel">
          <div className="panel-top">
            <h3 className="panel-h">Recent deals</h3>
            <button className="linkbtn" onClick={() => go("deals")}>All deals</button>
          </div>
          {deals!.length === 0 ? (
            <p className="hint">No deals yet.</p>
          ) : (
            <ul className="mini">
              {deals!.slice(0, 5).map((d) => (
                <li key={d.id}>
                  <span className="mini-a">
                    {d.buyer ? `${d.buyer.first_name} ${d.buyer.last_name}` : "No buyer"}
                    <em>{d.vehicle
                      ? `${d.vehicle.year} ${d.vehicle.make} ${d.vehicle.model}` : "—"}</em>
                  </span>
                  <span className="mini-b">
                    {formatMoneyShort(d.total_amount_due_cents)}
                    <em>{d.deal_no}</em>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="panel">
          <div className="panel-top">
            <h3 className="panel-h">Longest on the lot</h3>
            <button className="linkbtn" onClick={() => go("lot")}>Vehicles</button>
          </div>
          {stats.aged.length === 0 ? (
            <p className="hint">Nothing on the lot.</p>
          ) : (
            <ul className="mini">
              {stats.aged.slice(0, 5).map((v) => (
                <li key={v.id}>
                  <span className="mini-a">
                    {v.year} {v.make} {v.model}
                    <em>{v.stock_no}</em>
                  </span>
                  <span className="mini-b">
                    {days(v.purchase_date)}d
                    <em>{formatMoneyShort(v.asking_price_cents)}</em>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="quick">
        <button className="quick-b" onClick={() => go("addVehicle")}>Add a vehicle</button>
        <button className="quick-b" onClick={() => go("customers")}>Add a customer</button>
        <button className="quick-b" onClick={() => go("deals")}>Write a deal</button>
      </div>
    </>
  );
}
